import type { Command } from "../document/commands.ts";
import type { MapDocument, TileRef, WorldDocument } from "../document/types.ts";
import type { CellRect } from "../terrain/match.ts";
import { paintTilesCommand } from "./panel-commands.ts";
import type { Repaint } from "./store.ts";
import { TileEditing, type TileClipboard, type TileRefusal } from "./tile-editing.ts";
import { TileStrokes } from "./tile-stroke.ts";

/**
 * Tile authoring (ADR-033, ADR-044, ADR-089): the tile in hand and its mirrors, painting tile cells as one
 * command, the stroke that draws as it paints (`TileStrokes`), and the tile selection with its clipboard
 * (`TileEditing`). The hand and the selection are view state outside history.
 */

/**
 * What tile authoring needs from the store.
 */
export interface TileHost {
  readonly document: () => WorldDocument;
  readonly map: () => MapDocument;
  readonly activeLayerId: () => string | null;
  readonly isEditable: (layerId: string) => boolean;
  readonly sessionRefuses: () => boolean;
  readonly liveRefuses: () => boolean;
  readonly deselectStandIn: () => void;
  readonly run: (command: Command) => boolean;
  readonly notify: () => void;
  readonly emitRepaint: (repaint: Repaint) => void;
}

/**
 * What callers reach of tile authoring, through `EditorStore.tiles`.
 */
export type TileSlice = Pick<TileAuthoring, "brush" | "setBrush" | "paint" | "beginStroke" | "extendStroke" | "finishStroke" | "cancelStroke" | "selection" | "refusal" | "clipboard" | "setSelection" | "isSelected" | "pickTile" | "copy" | "paste" | "move" | "mirror" | "mirrorBrush" | "cut" | "remove">;

/**
 * The tile author.
 */
export class TileAuthoring {
  private tile: TileRef | null = null;

  /**
   * The tile selection on the active tile layer and the tile clipboard.
   */
  public readonly editing: TileEditing;

  /**
   * The tile stroke that draws as it paints.
   */
  public readonly strokes: TileStrokes;

  /**
   * @param host The store's view of itself.
   */
  public constructor(private readonly host: TileHost) {
    this.editing = new TileEditing({
      map: host.map,
      tilesets: () => host.document().tilesets,
      activeLayerId: host.activeLayerId,
      refusesEdits: (layerId) => !host.isEditable(layerId),
      run: (command) => host.run(command),
      notify: host.notify,
      taken: host.deselectStandIn,
    });
    this.strokes = new TileStrokes({
      document: host.document,
      map: host.map,
      tilesets: () => host.document().tilesets,
      editable: (layerId) => host.isEditable(layerId) && !host.sessionRefuses(),
      paint: (layerId, indices, ref) => {
        const command = paintTilesCommand(host.map(), layerId, indices, ref, host.document().tilesets);
        return command !== null && host.run(command);
      },
      repaint: (layerId, rect) => host.emitRepaint({ layerId, rect }),
    });
  }

  /**
   * The tile in hand for tile layers (ADR-033). View state outside history.
   *
   * @returns The tile, or null.
   */
  public get brush(): TileRef | null {
    return this.tile;
  }

  /**
   * Sets the tile in hand.
   *
   * @param ref The tile, or null for none.
   * @throws Error when the tile's tileset is not in the world.
   */
  public setBrush(ref: TileRef | null): void {
    if (ref !== null && !this.host.document().tilesets.some((t) => t.id === ref.tileset)) {
      throw new Error(`tileset ${ref.tileset} is not in the world`);
    }
    this.tile = ref;
    this.host.notify();
  }

  /**
   * Mirrors the tile in hand once more along an axis (ADR-089). View state outside history, as the hand is.
   *
   * @param axis `x` toggles the horizontal mirror, `y` the vertical.
   * @returns False when no tile is in hand.
   */
  public mirrorBrush(axis: "x" | "y"): boolean {
    const tile = this.tile;
    if (tile === null) {
      return false;
    }
    const flipX = axis === "x" ? tile.flipX !== true : tile.flipX === true;
    const flipY = axis === "y" ? tile.flipY !== true : tile.flipY === true;
    this.tile = { tileset: tile.tileset, tile: tile.tile, ...(flipX ? { flipX } : {}), ...(flipY ? { flipY } : {}) };
    this.host.notify();
    return true;
  }

  /**
   * Lets go of the tile in hand when the world no longer lists its tileset, telling no one.
   */
  public dropMissing(): void {
    if (this.tile !== null && !this.host.document().tilesets.some((t) => t.id === this.tile?.tileset)) {
      this.tile = null;
    }
  }

  /**
   * Paints cells of a tile layer with a tile as one command.
   *
   * @param layerId Tile layer id.
   * @param indices Cell indices.
   * @param ref Tile to place; the tile in hand by default, null erases.
   * @throws Error when the layer is not a tile layer, or no tile is in hand and none was given.
   */
  public paint(layerId: string, indices: readonly number[], ref?: TileRef | null): void {
    const tile = ref === undefined ? this.tile : ref;
    if (ref === undefined && tile === null) {
      throw new Error("no tile in hand");
    }
    const command = paintTilesCommand(this.host.map(), layerId, indices, tile, this.host.document().tilesets);
    if (command !== null) {
      this.host.run(command);
    }
  }

  /**
   * Starts a tile stroke that draws as it paints (ADR-054's amendment of 2026-10-01, decision 3): until it
   * ends, `layerCells` hands the layer's preview, which each extension writes and repaints, and the document
   * is untouched. Undo and redo wait for it, and any other document change drops it.
   *
   * @param layerId The tile layer.
   * @param ref The tile to write, or null to erase.
   * @returns False when the layer is not a tile layer of the map in view, is locked or hidden, or a stroke runs.
   * @throws Error when the tile's tileset is not in the document.
   */
  public beginStroke(layerId: string, ref: TileRef | null): boolean {
    return !this.host.liveRefuses() && this.strokes.begin(layerId, ref);
  }

  /**
   * Covers cells with the running tile stroke, repainting those it changed.
   *
   * @param indices Tile cell indices.
   * @throws RangeError when an index lies outside the map.
   */
  public extendStroke(indices: readonly number[]): void {
    this.strokes.extend(indices);
  }

  /**
   * Ends the running tile stroke, writing every covered cell as one command.
   *
   * @returns True when a command was recorded.
   */
  public finishStroke(): boolean {
    return this.strokes.finish();
  }

  /**
   * Drops the running tile stroke unwritten and repaints what it showed.
   */
  public cancelStroke(): void {
    this.strokes.cancel();
  }

  /**
   * The tile selection on the active tile layer (ADR-044), view state. It drops when the active layer
   * changes, when the active layer locks, and on undo or redo, so a rectangle never outlives the cells it
   * was drawn over and never sits on a layer that refuses edits.
   *
   * @returns The cell rectangle, or null.
   */
  public get selection(): CellRect | null {
    return this.editing.selection;
  }

  /**
   * Why the tile commands cannot run on the active layer (ADR-097 decision 22).
   *
   * @returns The reason while a terrain layer is active, else null.
   */
  public refusal(): TileRefusal | null {
    return this.editing.refusal();
  }

  /**
   * The tile clipboard (ADR-044).
   *
   * @returns The copied block, or null.
   */
  public get clipboard(): TileClipboard | null {
    return this.editing.clipboard;
  }

  /**
   * Whether a cell lies inside the tile selection.
   *
   * @param x Column.
   * @param y Row.
   * @returns True when selected.
   */
  public isSelected(x: number, y: number): boolean {
    return this.editing.contains(x, y);
  }

  /**
   * The tile at a cell of the active tile layer, for the eyedropper.
   *
   * @param index Cell index.
   * @returns The tile, or null.
   */
  public pickTile(index: number): TileRef | null {
    return this.editing.pickTile(index);
  }

  /**
   * Copies the tile selection into the clipboard.
   *
   * @returns False when nothing was copied.
   */
  public copy(): boolean {
    return this.editing.copy();
  }

  /**
   * Pastes the tile clipboard at a cell as one command.
   *
   * @param x Target column.
   * @param y Target row.
   * @returns False when nothing was pasted.
   */
  public paste(x: number, y: number): boolean {
    return this.editing.paste(x, y);
  }

  /**
   * Moves the tile selection by a delta as one command.
   *
   * @param dx Column delta.
   * @param dy Row delta.
   * @returns False when nothing moved.
   */
  public move(dx: number, dy: number): boolean {
    return this.editing.move(dx, dy);
  }

  /**
   * Mirrors the tile selection as a block as one command (ADR-089).
   *
   * @param axis `x` mirrors left to right, `y` top to bottom.
   * @returns False when there is no selection to mirror.
   */
  public mirror(axis: "x" | "y"): boolean {
    return this.editing.mirror(axis);
  }

  /**
   * Copies the tile selection and clears its cells as one command.
   *
   * @returns False when nothing was cut.
   */
  public cut(): boolean {
    return this.editing.cut();
  }

  /**
   * Clears the tile selection's cells as one command.
   *
   * @returns False when nothing was cleared.
   */
  public remove(): boolean {
    return this.editing.remove();
  }

  /**
   * Sets or clears the tile selection; a locked active layer takes none. A selection taken deselects the
   * stand-in.
   *
   * @param rect The rectangle in any corner order, or null to clear.
   */
  public setSelection(rect: CellRect | null): void {
    if (rect === null) {
      this.editing.clear();
    } else {
      this.editing.select(rect);
    }
  }
}
